import React from "react";
import { TransformedProduct } from "../../types/productApiTypes";

interface ProductPriceDisplayProps {
  product: TransformedProduct;
  /**
   * Optional: If a variation is selected (or active context), pass it here.
   * If provided, the component will display the variation's price unless logic dictates showing a range.
   */
  selectedVariation?: TransformedProduct | null;
  className?: string;
}

const ProductPriceDisplay: React.FC<ProductPriceDisplayProps> = ({
  product,
  selectedVariation,
  className = "",
}) => {
  // Determine which data source to use for price, sale, and stock status.
  // Priority: 1. A user-selected variation. 2. The parent product data.
  const displayData = selectedVariation || product;
  const isOnSale = displayData.onSale;

  // --- 1. Handle Empty/Invalid Price ---
  // If the formatted price is empty (handled in getProductPriceInfo), render nothing.
  if (!displayData.formattedPrice) {
    return null;
  }

  // --- 2. Price Range Logic ---
  // Only show the range if:
  // A) No specific variation is actively selected/passed (i.e., Archive/Card view context).
  // B) The product supports ranges (Variable/Grouped).
  // C) The range values exist.
  if (
    !selectedVariation &&
    (product.type === "grouped" || product.isVariable) &&
    product.formattedMinPrice &&
    product.formattedMaxPrice
  ) {
    return (
      <div className={`product-price ${className}`}>
        <span className="product-price__current">
          {product.formattedMinPrice} – {product.formattedMaxPrice}
        </span>
      </div>
    );
  }

  // --- 3. Sale Price Logic ---
  if (isOnSale) {
    return (
      <div className={`product-price product-price--on-sale ${className}`}>
        <del aria-label="Original price" className="product-price__regular">
          {displayData.formattedRegularPrice}
        </del>
        <ins aria-label="Sale price" className="product-price__current">
          {displayData.formattedSalePrice}
        </ins>
      </div>
    );
  }

  // --- 4. Standard Price Logic ---
  return (
    <div className={`product-price ${className}`}>
      <span className="product-price__current">
        {displayData.formattedPrice}
      </span>
    </div>
  );
};

export default ProductPriceDisplay;
